/**
 * Centralized Source Path Configuration
 *
 * Single source of truth for source path types used in indicators.
 * Supports both basic OHLC fields and calculated fields (hl/2, hlc/3, etc.)
 *
 * @module getSourcePath
 *
 * @example
 * import { SOURCE_PATH_TYPES, getSourcePathLabel, getSourcePathAccessor } from './utils';
 *
 * // Get all available source paths
 * const paths = SOURCE_PATH_TYPES;
 *
 * // Get label for display
 * const label = getSourcePathLabel('close'); // Returns "Fechamento"
 *
 * // Get value extractor function
 * const accessor = getSourcePathAccessor('close');
 * const value = accessor(dataPoint); // Returns dataPoint.close
 */

import { path } from "./index";

// PHASE 3: Module-level indicators context for ID→renderKey resolution
// This is set by the calculator pipeline before processing indicators
let _indicatorsContext = null;

/**
 * Set indicators context for ID→renderKey resolution during calculation
 * @param {Array} indicators - Chart indicators
 * @internal Used by calculator pipeline only
 */
export function setIndicatorsContext(indicators) {
  _indicatorsContext = indicators;
}

/**
 * Clear indicators context after calculation
 * @internal Used by calculator pipeline only
 */
export function clearIndicatorsContext() {
  _indicatorsContext = null;
}

/**
 * Available Source Path types with labels for UI
 * This is the single source of truth for source path configuration
 *
 * Structure:
 * - value: Internal identifier used in calculator options
 * - label: Portuguese label for UI display
 * - type: "basic" for direct OHLC fields, "calculated" for computed values
 * - formula: (optional) Function to calculate value from data point
 */
export const SOURCE_PATH_TYPES = [
  // Basic OHLC fields - direct access to data properties
  { value: "open", label: "Abertura", type: "basic" },
  { value: "high", label: "Máxima", type: "basic" },
  { value: "low", label: "Mínima", type: "basic" },
  { value: "close", label: "Fechamento", type: "basic" },

  // Calculated fields - computed values from OHLC data
  {
    value: "hl2",
    label: "HL/2 (Mediana)",
    type: "calculated",
    description: "Ponto médio entre máxima e mínima",
    formula: (d) => {
      if (!d || d.high === undefined || d.low === undefined) return undefined;
      if (!Number.isFinite(d.high) || !Number.isFinite(d.low)) return undefined;
      return (d.high + d.low) / 2;
    },
  },
  {
    value: "hlc3",
    label: "HLC/3 (Típico)",
    type: "calculated",
    description: "Preço típico: média de máxima, mínima e fechamento",
    formula: (d) => {
      if (
        !d ||
        d.high === undefined ||
        d.low === undefined ||
        d.close === undefined
      )
        return undefined;
      if (
        !Number.isFinite(d.high) ||
        !Number.isFinite(d.low) ||
        !Number.isFinite(d.close)
      )
        return undefined;
      return (d.high + d.low + d.close) / 3;
    },
  },
  {
    value: "ohlc4",
    label: "OHLC/4 (Média)",
    type: "calculated",
    description: "Média aritmética de abertura, máxima, mínima e fechamento",
    formula: (d) => {
      if (
        !d ||
        d.open === undefined ||
        d.high === undefined ||
        d.low === undefined ||
        d.close === undefined
      )
        return undefined;
      if (
        !Number.isFinite(d.open) ||
        !Number.isFinite(d.high) ||
        !Number.isFinite(d.low) ||
        !Number.isFinite(d.close)
      )
        return undefined;
      return (d.open + d.high + d.low + d.close) / 4;
    },
  },
  {
    value: "hlcc4",
    label: "HLCC/4 (Ponderado)",
    type: "calculated",
    description: "Preço ponderado: média de máxima, mínima e fechamento (2x)",
    formula: (d) => {
      if (
        !d ||
        d.high === undefined ||
        d.low === undefined ||
        d.close === undefined
      )
        return undefined;
      if (
        !Number.isFinite(d.high) ||
        !Number.isFinite(d.low) ||
        !Number.isFinite(d.close)
      )
        return undefined;
      return (d.high + d.low + d.close + d.close) / 4;
    },
  },
];

/**
 * Get the display label for a source path type
 * @param {string} sourcePathValue - Source path identifier (e.g., 'open', 'high', 'close')
 * @param {Array} [indicators] - Optional array of chart indicators for resolving indicator sources
 * @returns {string} Localized label (e.g., 'Abertura', 'Máxima', 'Fechamento')
 *
 * @example
 * getSourcePathLabel('close') // Returns 'Fechamento'
 * getSourcePathLabel('high')  // Returns 'Máxima'
 * getSourcePathLabel('INDICATOR-123.bollingerband', indicators) // Returns 'Bollinger Bands'
 */
export function getSourcePathLabel(sourcePathValue, indicators = null) {
  if (!sourcePathValue) return "Fechamento"; // Default to 'close' label

  const normalizedValue = sourcePathValue.toLowerCase().trim();

  // Find the configuration from SOURCE_PATH_TYPES array
  const sourcePathConfig = SOURCE_PATH_TYPES.find(
    (sp) => sp.value === normalizedValue,
  );

  if (sourcePathConfig) {
    return sourcePathConfig.label;
  }

  // Check if this is an indicator reference (contains a dot)
  if (sourcePathValue.includes(".")) {
    return prettifyIndicatorSource(sourcePathValue, indicators);
  }

  // Fallback: return uppercase version for unknown types
  return normalizedValue.toUpperCase();
}

/**
 * Prettify an indicator source path for display
 * Converts "INDICATOR-1766693124641-2.BOLLINGERBAND" to "Bollinger Bands" or similar
 *
 * @param {string} sourcePath - The raw source path (e.g., "uuid-123.rsi", "INDICATOR-xxx.bollingerband")
 * @param {Array} [indicators] - Optional array of chart indicators to look up display names
 * @returns {string} Prettified label
 */
export function prettifyIndicatorSource(sourcePath, indicators = null) {
  if (!sourcePath || !sourcePath.includes(".")) {
    return sourcePath || "Fechamento";
  }

  const [indicatorId, seriesKey] = sourcePath.split(".");

  // Try to find indicator by ID and get its displayName
  if (indicators && Array.isArray(indicators)) {
    const sourceIndicator = indicators.find((ind) => ind.id === indicatorId);
    if (sourceIndicator) {
      // Use displayName if available, otherwise use key
      const name =
        sourceIndicator.displayName || sourceIndicator.key || seriesKey;
      return name;
    }
  }

  // Fallback: prettify the series key
  // Convert "BOLLINGERBAND" -> "Bollinger Band", "rsi" -> "RSI"
  return prettifySeriesKey(seriesKey);
}

/**
 * Convert a series key to a readable name
 * @param {string} seriesKey - The series key (e.g., "bollingerband", "rsi", "ema")
 * @returns {string} Prettified name
 */
function prettifySeriesKey(seriesKey) {
  if (!seriesKey) return "";

  const key = seriesKey.toLowerCase();

  // Known indicator mappings
  const knownIndicators = {
    bollingerband: "Bollinger Bands",
    bollinger: "Bollinger Bands",
    rsi: "RSI",
    macd: "MACD",
    ema: "EMA",
    sma: "SMA",
    wma: "WMA",
    tema: "TEMA",
    dema: "DEMA",
    stochastic: "Estocástico",
    atr: "ATR",
    adx: "ADX",
    cci: "CCI",
    williamsr: "Williams %R",
    obv: "OBV",
    mfi: "MFI",
    volume: "Volume",
    signal: "Sinal",
    middle: "Média",
    upper: "Superior",
    lower: "Inferior",
  };

  if (knownIndicators[key]) {
    return knownIndicators[key];
  }

  // Default: capitalize first letter
  return seriesKey.charAt(0).toUpperCase() + seriesKey.slice(1).toLowerCase();
}

/**
 * Get value accessor function for a source path type
 * Returns a function that extracts the appropriate value from a data point
 *
 * @param {string} sourcePathValue - Source path identifier (e.g., 'open', 'close', 'hl2', 'uuid-123.rsi')
 * @returns {Function} Accessor function that takes a data point and returns the value
 *
 * @example
 * // Basic field access
 * const accessor = getSourcePathAccessor('close');
 * const closeValue = accessor(dataPoint); // Returns dataPoint.close
 *
 * // Calculated field access
 * const accessor = getSourcePathAccessor('hl2');
 * const midpoint = accessor(dataPoint); // Returns (dataPoint.high + dataPoint.low) / 2
 *
 * // Series reference (Phase 3: Series-as-Source)
 * // IMPORTANT: Now uses immutable indicator ID, resolved to current renderKey via context
 * const accessor = getSourcePathAccessor('uuid-123.rsi');
 * const rsiValue = accessor(dataPoint); // Returns dataPoint.rsi_14?.rsi (current renderKey)
 */
export function getSourcePathAccessor(sourcePathValue) {
  // Special case: undefined or null means identity function (for numeric arrays)
  // This is used by MACD signal line and other indicators that process numeric arrays
  if (sourcePathValue === undefined || sourcePathValue === null) {
    return (d) => d; // Identity function - return value as-is
  }

  // Empty string defaults to 'close'
  if (sourcePathValue === "") {
    return path("close");
  }

  const normalizedValue = sourcePathValue.toLowerCase().trim();

  // PHASE 3: Check for series reference (dot notation)
  // Format: "indicatorId.seriesKey" (e.g., "uuid-123.rsi", "uuid-456.signal")
  // CRITICAL: indicatorId is now the IMMUTABLE UUID, not the mutable renderKey
  if (normalizedValue.includes(".")) {
    const [indicatorId, seriesKey] = normalizedValue.split(".");

    // Return accessor that resolves ID → renderKey at calculation time
    return (d) => {
      if (!d) return undefined;

      // PHASE 3: Resolve immutable ID to current renderKey using module context
      // This allows RSI(14) → RSI(16) changes to work transparently
      let currentRenderKey = indicatorId; // Fallback to ID if no context

      if (_indicatorsContext && Array.isArray(_indicatorsContext)) {
        const sourceIndicator = _indicatorsContext.find(
          (ind) => ind.id === indicatorId,
        );
        if (sourceIndicator && sourceIndicator.renderKey) {
          currentRenderKey = sourceIndicator.renderKey;
        }
      }

      // Access nested indicator data using CURRENT renderKey
      // Example: d.rsi_16?.rsi (even though dependency was stored as uuid-123.rsi)
      const indicatorData = d[currentRenderKey];
      if (!indicatorData) return undefined;

      // For simple indicators (single value), seriesKey might match the indicator type
      // For multi-value indicators (MACD, BB, etc.), access the specific series
      const value = indicatorData[seriesKey];
      if (value !== undefined) return value;

      // The sub-series is missing. Distinguish two cases:
      //  - Scalar indicator stored directly (d[renderKey] is a number):
      //    fall back to it (seriesKey just doesn't name a field).
      //  - Multi-series object (MACD, BB, …) mid-warmup: the field isn't
      //    ready yet — return undefined, NOT the object. Returning the
      //    object made numeric consumers (e.g. EMA-of-MACD) compute NaN,
      //    poisoning the whole series and rendering as 0,00. (#341)
      return typeof indicatorData === "number" ? indicatorData : undefined;
    };
  }

  // Find the configuration from SOURCE_PATH_TYPES array
  const sourcePathConfig = SOURCE_PATH_TYPES.find(
    (sp) => sp.value === normalizedValue,
  );

  if (!sourcePathConfig) {
    // Fallback: use standard path accessor for unknown/legacy types
    // This ensures backward compatibility with custom source paths
    return path(sourcePathValue);
  }

  if (sourcePathConfig.type === "calculated") {
    // Return calculated formula with null/undefined safety built-in
    return sourcePathConfig.formula;
  }

  // Basic field: return standard path accessor
  return path(sourcePathConfig.value);
}

/**
 * Get filtered source path options based on type
 * Useful for indicators that only want basic or calculated fields
 *
 * @param {Object} options - Filter options
 * @param {boolean} options.includeBasic - Include basic OHLC fields (default: true)
 * @param {boolean} options.includeCalculated - Include calculated fields (default: false)
 * @returns {Array} Filtered array of source path configurations
 *
 * @example
 * // Get only basic fields (default behavior for Phase 1)
 * const basicFields = getSourcePathOptions();
 * // Returns [open, high, low, close]
 *
 * // Future Phase 3: Get all fields including calculated
 * // const allFields = getSourcePathOptions({ includeCalculated: true });
 * // Returns [open, high, low, close, hl2, hlc3, ohlc4]
 */
export function getSourcePathOptions({
  includeBasic = true,
  includeCalculated = false,
} = {}) {
  return SOURCE_PATH_TYPES.filter((sp) => {
    if (sp.type === "basic" && includeBasic) return true;
    if (sp.type === "calculated" && includeCalculated) return true;
    return false;
  });
}
